// this file should be used to store global swal configurations that can be reused across the site
// if you need to only modify one (or a few keys) in an object, you can use the following syntax
// to retain some reusability: Object.assign({}, originalConfig, { overideKey: overideValue })

const swalConfigs = {
    deleteDraft: {
        prompt: {
            title: "Are you sure you want to delete this draft campaign?",
            icon: "warning",
            buttons: {
                cancel: "Cancel",
                confirm: {
                    text: "Delete Draft",
                    closeModal: false,
                },
            },
        },
        fail: {
            title: "Error",
            icon: "error",
            text: "There was an error deleting this campaign.",
        },
    },
    missingEmailTemplate: {
        title: "Missing Email Template",
        text: "An email template is required to send confirmation emails",
        icon: "error",
    },
    saveDraft: {
        buttons: ["Continue Editing", "Go Back"],
        title: "Updated",
        text: "Your campaign was updated successfully.",
        icon: "success",
    },
    saveDraftError: {
        title: "Error",
        text: "There was an error saving your campaign.",
        icon: "error",
    },
    campaignValidationError: {
        title: "Oops!",
        text: "It looks like you're missing something!",
        icon: "warning",
    },
    defaultLists: {
        title: "Warning",
        text: "You did not choose any default lists for targets.",
        icon: "warning",
    },
    subjectRequired: {
        title: "Warning",
        text: "You must provide a subject for your write campaign message.",
        icon: "error",
    },
    endCampaign: {
        buttons: {
            cancel: "Don't End",
            confirm: {
                text: "End Campaign",
                closeModal: false,
                value: true,
            },
        },
        title: "Warning",
        text: "Are you sure you want to mark the campaign as ended? This will remove it from the Action Center and participants will no longer be able to participate. You can always restart the campaign later.",
        icon: "warning",
    },
    successfulEndCampaign: {
        title: "Campaign Ended",
        text: "Your campaign was successfully ended.",
        icon: "success",
    },
    failEndCampaign: {
        title: "Error",
        text: "We're sorry, we were unable to end your campaign",
        icon: "error",
    },
    failLaunch: {
        title: "Error",
        text: "We're sorry, we were unable to launch your campaign",
        icon: "error",
    },
    launchCampaign: {
        title: "Ready to Launch?",
        text: "Are you sure want to launch this campaign? This will deliver any actions created while this campaign was a draft unless you have archived them.",
        icon: "info",
        buttons: {
            cancel: "Cancel",
            confirm: {
                text: "Launch Campaign",
                closeModal: false,
                value: true,
            },
        },
    },
    cancelCampaign: {
        buttons: {
            cancel: { text: "Return to Campaign", value: false, visible: true },
            confirm: {
                text: "Cancel",
                value: true,
            },
        },
        title: "Warning",
        text: "Are you sure you want to cancel your campaign?",
        icon: "warning",
    },
    reopenCampaign: {
        prompt: {
            buttons: {
                cancel: "Keep Completed",
                confirm: {
                    text: "Reopen",
                    closeModal: false,
                    value: true,
                },
            },
            title: "Warning",
            text: "Are you sure you want to reopen the campaign? This will make it visible to participants.",
            icon: "warning",
        },
        success: {
            title: "Campaign Reopened",
            text: "Your campaign was successfully reopened!",
            icon: "success",
        },
        fail: {
            title: "Error",
            text: "We're sorry, we were unable to end your campaign",
            icon: "error",
        },
    },
    successfulLaunch: {
        title: "Launched",
        text: "Your campaign was launched successfully and is now available in the Action Center.",
        icon: "success",
    },
    invalidPlaceholders: (errors) => ({
        title: "Invalid Placeholders",
        icon: "error",
        text: `The following are invalid placeholders: ${errors.join("\n")}`,
    }),
    grassrootsRegistrationPageArchive: {
        prompt: {
            icon: "warning",
            title: "Archive Registration Page?",
            buttons: ["Cancel", "Archive"],
        },
        fail: {
            title: "Error",
            text: "There was a problem archiving the Registration Form. Please contact us at support@quorum.us",
            icon: "error",
        },
    },
    confirmationEmailInvalid: {
        title: "Warning",
        text: "The confirmation email has a missing or invalid field",
        icon: "error",
    },
    policyAreaBlank: {
        title: "Error",
        text: "Please choose a policy area for this campaign (Step 1: Name and Type of Campaign).",
        icon: "error",
    },
    noSingleBodyIfEditableFieldHasTargetPlaceholder: {
        title: "Error",
        icon: "error",
        content: (() => {
            const span = document.createElement("div")
            span.innerHTML =
                "<p>You have a toggle for <b>&lsquo;Should advocates only see a single message " +
                "to send?&rsquo;</b> set to <b>&lsquo;YES&rsquo;</b>, however one of your message bodies or subject " +
                "lines contains a target placeholder.</p><br/>" +
                "<p>If you wish to use target placeholders, you must show the advocates a different message for each " +
                "target by setting the toggle to <b>&lsquo;OFF&rsquo;</b>. This toggle can be found in the Message " +
                "Settings (4) under the advanced options dropdown.</p>"
            return span
        })(),
    },
    backgroundColorInvalid: {
        title: "Error",
        text: "The color you chose for your background is not a valid hex value.",
        icon: "error",
    },
    backgroundColorMissing: {
        title: "Error",
        text: "You selected your background to be a color but you have not yet selected a valid color.",
        icon: "error",
    },
    backgroundImageMissing: {
        title: "Error",
        text: "You selected your background to be an image but you have not yet uploaded an image.",
        icon: "error",
    },
    fullBleedHeaderImageMissing: {
        title: "Error",
        text: "You selected your full bleed header to be an image but you have not yet uploaded an image.",
        icon: "error",
    },
    fullBleedHeaderVideoMissing: {
        title: "Error",
        text: "You selected your full bleed header to be an video but you have not input a valid video link.",
        icon: "error",
    },
    emptyDisclaimerText: {
        title: "Error",
        icon: "error",
        content: (() => {
            const div = document.createElement("div")
            div.innerHTML =
                "<p>Disclaimer Text is required for PAC solicitations.</p>" +
                "<p>View the FEC website for details on " +
                '<a href="https://www.fec.gov/help-candidates-and-committees/fundraising-for-ssf/notices-required-ssf-solicitations/" rel="noopener" target="_blank">SSF</a>' +
                " and " +
                '<a href="https://www.fec.gov/help-candidates-and-committees/making-disbursements-pac/notices-required-nonconnected-solicitations/" rel="noopener" target="_blank">nonconnected PAC</a>' +
                " solicitation notices.</p>"
            return div
        })(),
    },
    invalidRegulationId: {
        title: "Error",
        text:
            "The provided Regulation ID is invalid. " +
            "Please input the full Document ID for the proposed rule as provided by " +
            "regulations.gov",
        icon: "error",
    },
    regulationCommentingClosed: (closedRegulationId, closedRegulationTitle) => ({
        title: "Error",
        icon: "error",
        text: `The regulation ${closedRegulationId}: ${closedRegulationTitle} is currently closed for commenting.`,
    }),
}

export default swalConfigs
